import { useContext, useState } from "react";
import styled from "styled-components";
import client from "api/WookAxios";
import ModalContext from "components/contexts/ModalContext";
import log from "loglevel";
import { parseErrors } from "lib/errors/parse";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { handleThrottle } from "lib/errors/handle";
import { useVerification } from "../useVerification";
import { VERIFICATION_CHANNEL } from "constants/auth";
import { FindAccountResult } from "./FindAccountResult";
import { FindAccountVerify } from "./FindAccountVerify";
import { sessionStorageAdapter } from "utils/storage";
import { RECOVERY_STORAGE_KEY } from "constants/storage";

const ModalFrame = styled.div`
  // display: ${(props) => (props.$visible ? "flex" : "none")};
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgb(45, 49, 45, 0.5);
  z-index: 1010;
`;

const Modal = styled.div`
  position: relative;
  margin: 0 auto;
  top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px;
  width: 350px;
  height: 400px;
  background: #fafbfa;
  background: white;
  opacity: 1;
`;

const StepDisplay = styled.div`
  display: ${({ $show }) => $show ? 'flex' : 'none'};
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const STEP = {
  VERIFY: 'verify',
  RESULT: 'result',
};

export const FindAccountPage = () => {
  const { modalActions } = useContext(ModalContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [step, setStep] = useState(STEP.VERIFY);
  const [accounts, setAccounts] = useState(null);
  const [channel, setChannel] = useState(() => 
    sessionStorageAdapter.load(RECOVERY_STORAGE_KEY.FIND_ACCOUNT_CHANNEL) ??
    VERIFICATION_CHANNEL.EMAIL
  );
  
  const email = useVerification(VERIFICATION_CHANNEL.EMAIL);
  const mobile = useVerification(VERIFICATION_CHANNEL.MOBILE);

  const handleSendCode = async (e, verification) => {
    e.preventDefault();
    await verification.sendCode();
  };

  const handleEmailVerify = async (e) => {
    e.preventDefault();
    
    const target = email.validateTarget();
    if (!target) return;

    const verificationCode = email.validateCode();
    if (!verificationCode) return;

    const payload = {
      channel: email.channel,
      target,
      verification_code: verificationCode,
    };

    await findAccount(payload);    
  };

  const handleMobileVerify = async (e) => {
    e.preventDefault();
    
    const target = mobile.validateTarget();
    if (!target) return;
    
    const verificationCode = mobile.validateCode();
    if (!verificationCode) return;

    const payload = {
      channel: mobile.channel,
      target,
      verification_code: verificationCode,
    };

    await findAccount(payload);
  };

  const findAccount = async (payload) => {
    try {
      const response = await client.findAccounts(payload);
      log.debug('FIND_ACCOUNTS_SUCCESS', response);
      setAccounts(response.data);
      clearCodeContext();
      setStep(STEP.RESULT);
    } catch(e) {
      log.debug('FIND_ACCOUNT_FAILED', e);
      const throttled = handleThrottle(e.response);
      if (throttled) return;
      const errors = parseErrors(e.response);
      toast.error(errors[0].message);
    }
  };

  const handleChannelChange = channel => {
    setChannel(channel);
    sessionStorageAdapter.save(RECOVERY_STORAGE_KEY.FIND_ACCOUNT_CHANNEL, channel);
  };

  const handleResetPassword = (account) => {
    clearVerification();

    sessionStorageAdapter.save(RECOVERY_STORAGE_KEY.RESET_PASSWORD_CHANNEL, channel);

    navigate('/reset-password', {
      state: {
        account,
        from: location.state?.from,
      },
    });
  };
  
  const clearCodeContext = () => {
    email.resetCodeContext();
    mobile.resetCodeContext();
  };

  const clearVerification = () => {
    email.reset();
    mobile.reset();
    sessionStorageAdapter.remove(RECOVERY_STORAGE_KEY.FIND_ACCOUNT_CHANNEL);
  };
  
  const handleClose = () => {
    clearVerification();
    modalActions.openLogin();
    navigate(location.state?.from ?? '/');
  };
    
  return (
    <>
      <ModalFrame>
        <Modal>
          <StepDisplay $show={step === STEP.VERIFY}>
            <FindAccountVerify
              email={email}
              mobile={mobile}
              channel={channel}
              onSendCode={handleSendCode}
              onEmailVerify={handleEmailVerify}
              onMobileVerify={handleMobileVerify}
              onChannelChange={handleChannelChange}
              onClose={handleClose}            
            />
          </StepDisplay>
          <StepDisplay $show={step === STEP.RESULT}>
            <FindAccountResult
              accounts={accounts}
              onResetPassword={handleResetPassword}
              onBack={() => setStep(STEP.VERIFY)}
              onClose={handleClose}
            />
          </StepDisplay>
        </Modal>
      </ModalFrame>
    </>
  );
};